import React, { useMemo } from 'react'
import { Spinner } from 'react-bootstrap'

import { extendCopyContentWithComplianceMessage, writeCopyContentToClipboard } from '../../../../../common/copy'
import { useEffectiveConfig } from '../../../../../common/useEffectiveConfig'

import { Task } from '../../../../../models/MeetingSupport'
import { User } from '../../../../../models/User'

import { NewFormTask, UserGroupTasks } from '../../../../../views/Tasks/Tasks'

import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectTaskPollingEnabled } from '../../../../../store/task/task'
import { useGetClientsPreviousMeetingTasksQuery } from '../../store/fa'
import { addToast } from '../../../../../store/toast/toastSlice'

import { copyGroupedTasks } from '../../MeetingSummary/MeetingResults/CustomDisplay/ClientAndAdvisorTasks/utils'

import { useTaskSelectionHandler } from '../../common/hooks/useTaskSelectionHandler'

import './Tasks.scss'

const EmptyState: React.FC = () => {
  return (
    <div className='bg-white border rounded-3 p-3'>
      <div className='d-flex align-items-center justify-content-center'>
        <h5 className='mb-0'>All caught up! No open tasks with these participants.</h5>
      </div>
    </div>
  )
}

interface TaskListProps {
  groupedTasks: { tasks: Task[]; user: User }[]
  isLoading: boolean
}

const TaskList: React.FC<TaskListProps> = ({ groupedTasks, isLoading }) => {
  const roomSession = useAppSelector(selectRoomSession)
  const isPolling = useAppSelector(selectTaskPollingEnabled)
  const dispatch = useAppDispatch()

  const { selectionHandlers } = useTaskSelectionHandler()
  const { copyComplianceMessage, accountIsFetching } = useEffectiveConfig()

  const contactsForMeeting = useMemo(() => roomSession?.users.filter((e) => e.type === 'external') ?? [], [roomSession?.users])

  if (!roomSession) {
    return null
  }
  const { roomId, id: sessionId, roomCreatedBy, users } = roomSession

  const initialRelatedToUser = contactsForMeeting.length === 1 ? contactsForMeeting[0] : null

  const userIds = new Set([...users.map((user) => user.id), ...(roomCreatedBy ? [roomCreatedBy.id] : [])])

  const groupedTasksForMeetingParticipants = groupedTasks.filter(({ user }) => userIds.has(user.id))

  const formTaskToCreateTaskPayload = (assignedToUserId: string, formTask: NewFormTask) => {
    const { subject, description, relatedToUserId } = formTask
    return {
      ...(relatedToUserId ? { relatedTo: { type: 'user', id: relatedToUserId } } : {}),
      ...(description ? { description } : {}),
      assignedTo: { type: 'user', id: assignedToUserId },
      task: subject,
      type: 'meeting',
      roomId,
      sessionId,
    }
  }

  const handleCopyTasks = async () => {
    try {
      const copyContent = extendCopyContentWithComplianceMessage(copyGroupedTasks(groupedTasksForMeetingParticipants), copyComplianceMessage)
      await writeCopyContentToClipboard(copyContent)
      dispatch(addToast({ variant: 'success', title: 'Copied', messages: ['Tasks copied to the clipboard'] }))
    } catch (e) {
      console.error('Failed to copy tasks to clipboard:', e)
      dispatch(addToast({ variant: 'error', title: 'Copy failed', messages: ['Failed to copy tasks to clipboard'] }))
    }
  }

  if (isLoading || isPolling) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  if (groupedTasksForMeetingParticipants.length === 0) {
    return <EmptyState />
  }

  return (
    <div className='bg-white border rounded-3 p-3 pb-4'>
      <div className='d-flex align-items-center justify-content-between mb-3 ps-3'>
        <h5 className='mb-0'>Open tasks from previous meeting</h5>
        <button
          className='btn btn-outline-secondary btn-sm d-flex align-items-center gap-2 paragraph-small bold text-dark'
          onClick={handleCopyTasks}
          disabled={accountIsFetching}
        >
          <i className='bi bi-files' />
          Copy all
        </button>
      </div>
      <div className='d-flex flex-column gap-2'>
        {groupedTasksForMeetingParticipants.map(({ user, tasks }, index) => (
          <div key={user.id} className='bg-white border rounded-3 p-3 pb-4'>
            <UserGroupTasks
              user={user}
              tasks={tasks}
              row={index}
              formTaskToCreateTaskPayload={formTaskToCreateTaskPayload}
              defaultRelatedToOptions={users}
              initialRelatedToUser={initialRelatedToUser}
              selectionHandlers={selectionHandlers}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

interface TasksProps {
  accountId: string
  clients: User[]
}

export function Tasks({ accountId, clients }: TasksProps) {
  const { data: groupedTasks = [], isLoading } = useGetClientsPreviousMeetingTasksQuery({ accountId, clients }, { skip: clients.length === 0 })
  return <TaskList groupedTasks={groupedTasks} isLoading={isLoading} />
}
